/**
 * Public surface of the network feature (see CONTRACT.md). The shell mounts `networkRoutes`, places `networkNavItems`
 * in the nav, and renders the components where the contract says.
 */
import { createElement, type ComponentType } from "react";
import type { RouteObject } from "react-router-dom";
import { Bot, Handshake, MessageSquare, Network, Users, type LucideIcon } from "lucide-react";
import { MatchesPage } from "./pages/MatchesPage";
import { MatchDetailPage } from "./pages/MatchDetailPage";
import { ItemsPage } from "./pages/ItemsPage";
import { NetworkPage } from "./pages/NetworkPage";
import { ProfilePage } from "./pages/ProfilePage";
import { MessagesPage } from "./pages/MessagesPage";
import { AskPage } from "./pages/AskPage";

export const networkRoutes: RouteObject[] = [
  { path: "/matches", element: createElement(MatchesPage) },
  { path: "/matches/:matchId", element: createElement(MatchDetailPage) },
  { path: "/network", element: createElement(NetworkPage) },
  { path: "/network/:userId", element: createElement(ProfilePage) },
  { path: "/needs", element: createElement(ItemsPage, { kind: "need" }) },
  { path: "/services", element: createElement(ItemsPage, { kind: "service" }) },
  { path: "/messages", element: createElement(MessagesPage) },
  { path: "/messages/:conversationId", element: createElement(MessagesPage) },
  { path: "/ask", element: createElement(AskPage) },
];

export interface NetworkNavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  badgeKey?: "matchesWaiting" | "messagesUnread" | "sharesPending";
}

// Lucide icons take size as number or string; the contract's icon type is the narrower number-only form.
const icon = (i: LucideIcon): NetworkNavItem["icon"] => i as unknown as NetworkNavItem["icon"];

export const networkNavItems: NetworkNavItem[] = [
  { to: "/matches", label: "Introductions", icon: icon(Handshake), badgeKey: "matchesWaiting" },
  { to: "/network", label: "Network", icon: icon(Network), badgeKey: "sharesPending" },
  { to: "/messages", label: "Messages", icon: icon(MessageSquare), badgeKey: "messagesUnread" },
  { to: "/ask", label: "Ask", icon: icon(Bot) },
];

export { ContactNetworkSection } from "./ContactNetworkSection";
export { HomeMatchesWidget } from "./HomeMatchesWidget";
export { NetworkCommandItems, type CommandItem } from "./commands";
export { useNetworkBadges } from "./badges";
export { NetworkOnboardingStep } from "./NetworkOnboardingStep";
export { AskPanel } from "./AskPanel";
export { ContactNeedsOffersStrip, helpCounts } from "./ContactNeedsOffersStrip";
export { CreditChip } from "./components/CreditChip";
export { PerspectiveHelp } from "./components/PerspectiveHelp";
export { useMatchCards, useCredits, useContactShares, useHubUserOf } from "./lib/hooks";
export { PeopleYouKnow, usePeopleYouKnow } from "./components/PeopleYouKnow";
export { ShareContactSheet } from "./components/ShareContactSheet";
export { SHARED_BY_FIELD } from "./lib/contactPayload";
export { cardInvolvesContact } from "./lib/cards";
